import addOnSVG from '@plone/volto/icons/add-on.svg';
import groupSVG from '@plone/volto/icons/group.svg';
import MpfPermissoesLoader from 'volto-ministerio-publico-federal/components/MpfPermissoesLoader/MpfPermissoesLoader';
import NotificacoesPoller from 'volto-ministerio-publico-federal/components/manage/Notificacoes/NotificacoesPoller';
import CookieConsent from 'volto-ministerio-publico-federal/components/CookieConsent';
import PageViewTracker from 'volto-ministerio-publico-federal/components/Analytics/PageViewTracker';
import annotationSVG from '@plone/volto/icons/annotation.svg';
import audioSVG from '@plone/volto/icons/audio.svg';
import bellSVG from '@plone/volto/icons/bell.svg';
import cardsSVG from '@plone/volto/icons/cards.svg';
import chunkSVG from '@plone/volto/icons/chunk.svg';
import eventSVG from '@plone/volto/icons/event.svg';
import navSVG from '@plone/volto/icons/nav.svg';
import newsSVG from '@plone/volto/icons/news.svg';
import showcaseSVG from '@plone/volto/icons/showcase.svg';
import videoCameraSVG from '@plone/volto/icons/videocamera.svg';

import homeBannerSVG from '@plone/volto/icons/image-wide.svg';
import type { ConfigType } from '@plone/registry';

import { ROUTES } from './routes.constants';
import { blocoNaoExibido } from 'volto-ministerio-publico-federal/customizations/mpf-slots-frontend/components/theme/SlotRenderer/SlotRenderer';
// Imagem institucional usada no card de compartilhamento quando o conteúdo não
// tem imagem própria — o caso da home e das páginas internas. É um placeholder
// gerado a partir das cores e da fonte do design; a arte definitiva depende de
// aprovação da comunicação.
import fallbackOgImage from 'volto-ministerio-publico-federal/theme/assets/img/og-default.png';

export const updateSettingsConfig = (config: ConfigType) => {
  config.settings = {
    ...config.settings,
    // Editor de slots: o componente que a lateral não mostra (ex.: o menu de
    // O MPF quando vale o de Unidades) aparece compacto, sem prévia.
    slotsBlockNotShown: (id: string, formData: Record<string, unknown>) =>
      blocoNaoExibido(
        id,
        formData,
        config.settings.mpfSlotsBlocosUnicos as string[] | undefined,
      ),
    // Tela "Minhas Pendências" é uma rota virtual (não é um conteúdo Plone),
    // então entra na lista de nonContentRoutes para o App não tentar resolvê-la
    // como conteúdo (getBaseUrl/SSR).
    nonContentRoutes: [
      ...(config.settings.nonContentRoutes || []),
      ROUTES.MINHAS_PENDENCIAS,
      ROUTES.PENDENCIAS_LEGADAS,
      ROUTES.AUDITAR_ACERVO,
      ROUTES.NOTIFICACOES,
    ],
    // 4 níveis, e 4 é o mínimo: o @navigation conta a profundidade a partir da
    // raiz de navegação, incluindo o item de topo. O mega menu deriva as
    // colunas de `[item, activePath[0], activePath[1]]` (Header/MegaMenu.tsx),
    // então o nível 1 é a barra e os níveis 2, 3 e 4 são as três colunas do
    // drill-down. Com menos que 4 a última coluna simplesmente não existe e os
    // itens do nível de corte viram folhas sem seta — em silêncio, porque a
    // coluna vazia é descartada sem erro. Para enxugar o payload da navegação,
    // cortar campo no @navigation (o backend já faz), não profundidade.
    navDepth: 4,
    qtdNotifications: 5,
    isMultilingual: false,
    supportedLanguages: ['pt-br'],
    defaultLanguage: 'pt-br',
    showTags: false,
    mpfSeo: {
      fallbackImageUrl: fallbackOgImage,
    },

    addContentTypesOrder: [
      'Folder',
      'Document',
      'News',
      'File',
      'Image',
      'Collection',
      'Link',
      'PastaServicos',
      'Servico',
      'Locations',
      'EasyForm',
      'Home',
      'Highlights',
      'Sitemap',
    ],
    contentIcons: {
      ...config.settings.contentIcons,
      Audio: audioSVG,
      Communication: addOnSVG,
      Events: eventSVG,
      GestorResponsavel: chunkSVG,
      Highlights: cardsSVG,
      Home: showcaseSVG,
      News: newsSVG,
      Notifications: bellSVG,
      Notification: annotationSVG,
      Sitemap: navSVG,
      Video: videoCameraSVG,
      HomeBanner: homeBannerSVG,
    },
    workflowMapping: {
      ...(config.settings.workflowMapping as Record<string, unknown>),
      visible: { value: 'visible', color: '#007bc1' },
      hide: { value: 'hide', color: '#ed4033' },
      show: { value: 'visible', color: '#007bc1' },
      reject: { value: 'visible', color: '#007bc1' },
      retract: { value: 'visible', color: '#007bc1' },
      // Fluxo editorial de notícias (regional -> nacional): estados...
      em_revisao_regional: { value: 'em_revisao_regional', color: '#f6a808' },
      aguardando_validacao: { value: 'aguardando_validacao', color: '#f6a808' },
      revisao_textual_nacional: {
        value: 'revisao_textual_nacional',
        color: '#0b7a75',
      },
      revisao_tecnica: { value: 'revisao_tecnica', color: '#0b7a75' },
      revogado: { value: 'revogado', color: '#b4442c' },
      // ...e as transições, pelo estado a que levam.
      enviar_revisao: { value: 'em_revisao_regional', color: '#f6a808' },
      cancelar_revisao: { value: 'private', color: '#ed4033' },
      concluir_revisao: { value: 'aguardando_validacao', color: '#f6a808' },
      devolver_revisao: { value: 'em_revisao_regional', color: '#f6a808' },
      publicar_local: { value: 'visible', color: '#007bc1' },
      enviar_revisao_nacional: {
        value: 'revisao_textual_nacional',
        color: '#0b7a75',
      },
      encaminhar_tecnica: { value: 'revisao_tecnica', color: '#0b7a75' },
      efetivar: { value: 'published', color: '#007bc1' },
      revogar: { value: 'revogado', color: '#b4442c' },
      // Fluxo editorial v2.6: etapas opcionais e revisão de conteúdo publicado.
      publicado_em_revisao: { value: 'publicado_em_revisao', color: '#007bc1' },
      publicado_aguardando_validacao: {
        value: 'publicado_aguardando_validacao',
        color: '#007bc1',
      },
      publicar_local_direto: { value: 'visible', color: '#007bc1' },
      publicar_nacional: { value: 'published', color: '#007bc1' },
      publicar_nacional_direto: { value: 'published', color: '#007bc1' },
      iniciar_revisao_publicada: {
        value: 'publicado_em_revisao',
        color: '#007bc1',
      },
      concluir_revisao_publicada: {
        value: 'publicado_aguardando_validacao',
        color: '#007bc1',
      },
      cancelar_revisao_publicada: { value: 'published', color: '#007bc1' },
      aceitar_alteracoes: { value: 'published', color: '#007bc1' },
      rejeitar_alteracoes: { value: 'published', color: '#b4442c' },
    },
    controlpanels: [
      ...((config.settings.controlpanels as unknown[]) ?? []),
      {
        '@id': '/mpf-version-profile',
        group: 'General',
        title: 'Versão do site (MPF)',
      },
      {
        '@id': '/mpf-permissoes',
        group: 'Users',
        title: 'Perfis de acesso',
      },
    ],
    controlPanelsIcons: {
      ...config.settings.controlPanelsIcons,
      'mpf-version-profile': addOnSVG,
      'mpf-permissoes': groupSVG,
    },
    appExtras: [
      ...((config.settings.appExtras as unknown[]) ?? []),
      { match: '', component: MpfPermissoesLoader },
      // Contador do sino de notificações (polling; ver o componente).
      { match: '', component: NotificacoesPoller },
      { match: '', component: CookieConsent },
      // AB#12099: pageview das navegações internas para o GTM.
      { match: '', component: PageViewTracker },
    ],
  } as ConfigType['settings'];
};

export default updateSettingsConfig;
